<template>
  <div>
    <!-- Selección del método de pago -->
    <div v-if="mode === 'select'" class="grid grid-cols-2 gap-4">
      <button 
        v-for="method in payment_methods" 
        :key="method.id"
        @click="selectPaymentMethod(method)"
        class="btn-blue p-4 text-lg font-medium rounded-lg hover:opacity-90 transition-opacity"
      >
        {{ method.name }}
      </button>
    </div>
    
    <!-- Mensaje si no hay métodos de pago disponibles -->
    <div v-if="mode === 'select' && (!payment_methods || payment_methods.length === 0)" class="text-center p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
      No se encontraron métodos de pago configurados.
      <pre>{{ payment_methods }}</pre>
    </div>

    <!-- Formulario de pago -->
    <div v-if="mode === 'form'" class="space-y-6">
      <form @submit.prevent="processPay">
        <!-- Campo monto en Bs si aplica -->
        <div class="mb-4">
          <text-input 
            v-if="payment_method.change_currency_id"
            v-model="amountAlternate"
            type="number"
            step="0.01"
            label="Monto en Bolívares"
            placeholder="Ingrese el monto en Bs"
            :error="errors.amount_bs"
          />
        </div>

        <!-- Campo monto principal -->
        <div class="mb-4">
          <text-input
            v-model="form.amount"
            :disabled="payment_method.change_currency_id"
            type="number" 
            step="0.01"
            label="Monto en USD"
            placeholder="Ingrese el monto en USD"
            :error="errors.amount"
          />
        </div>

        <!-- Campo referencia si aplica -->
        <div class="mb-4">
          <text-input
            v-if="!payment_method.change"
            v-model="form.reference"
            label="Referencia"
            placeholder="Ingrese el número de referencia"
            :error="errors.reference"
          />
        </div>

        <!-- Mostrar cambio si excede el balance -->
        <div v-if="hasChange" class="p-4 bg-blue-50 rounded-lg mb-4">
          <p class="text-md font-bold text-blue-900">
            Cambio a devolver: $ {{ changeAmount.toFixed(2) }} (Bs. {{ changeAmount.toFixed(2) * change }})
          </p>
        </div>

        <!-- Botones de acción -->
        <div class="flex items-center justify-end gap-4">
          <button 
            type="button"
            @click="cancelPayment"
            class="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            Cancelar
          </button>
          <button 
            type="submit"
            class="px-6 py-2 bg-teal-800 text-white rounded-lg hover:bg-teal-900 transition-colors"
          >
            Procesar pago
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import TextInput from './TextInput.vue'

export default {
  components: { 
    TextInput 
  },

  props: {
    payment_methods: {
      type: Array,
      required: true
    },
    balance: {
      type: Number,
      required: true
    },
    change: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      mode: 'select',
      payment_method: null,
      form: this.$inertia.form({
        amount: '',
        reference: '',
        exchange_rate: '',
        amount_bs: ''
      }),
      amountAlternate: '',
      errors: {}
    }
  },
  
  created() {
    // Verificar si hay métodos de pago disponibles
    // console.log('Payment methods received:', this.payment_methods)
    if (!this.payment_methods || this.payment_methods.length === 0) {
      console.warn('No hay métodos de pago registrados.')
    }
  },

  computed: {
    hasChange() {
      // Convertir a número para evitar problemas de comparación
      const balanceNum = parseFloat(this.balance) || 0;
      const amountNum = parseFloat(this.form.amount) || 0;
      return balanceNum < amountNum;
    },

    changeAmount() {
      // Convertir a número para evitar problemas de cálculo
      const balanceNum = parseFloat(this.balance) || 0;
      const amountNum = parseFloat(this.form.amount) || 0;
      return Math.max(0, amountNum - balanceNum);
    }
  },

  watch: {
    amountAlternate(val) {
      // Manejar valores vacíos o inválidos
      if (!val || val === '') {
        this.form.amount = '';
        this.form.exchange_rate = '';
        this.form.amount_bs = '';
        return;
      }
      
      // Prevenir división por cero y convertir a número
      const changeRate = parseFloat(this.change);
      if (changeRate && changeRate > 0) {
        const parsedVal = parseFloat(val);
        if (!isNaN(parsedVal) && parsedVal > 0) {
          this.form.amount = (parsedVal / changeRate).toFixed(2);
          this.form.exchange_rate = changeRate;
          this.form.amount_bs = parsedVal;
        } else {
          this.form.amount = '';
          this.form.exchange_rate = '';
          this.form.amount_bs = '';
        }
      } else {
        this.form.amount = '';
        this.form.exchange_rate = '';
      }
    },
  },

  methods: {
    resetForm() {
      this.mode = 'select'
      this.payment_method = null
      this.form = this.$inertia.form({
        amount: '',
        reference: '',
        exchange_rate: '',
        amount_bs: ''
      })
      this.amountAlternate = ''
      
      // Limpiar errores de validación
      this.form.clearErrors()
      this.errors = {}
    },

    selectPaymentMethod(method) {
      this.mode = 'form'
      this.payment_method = method
    },

    cancelPayment() {
      this.mode = 'select'
      this.resetForm()
    },

    validatePayment() {
      // Reiniciar errores
      this.errors = {}

      // Si el método requiere referencia y no hay referencia, mostrar error
      if (!this.payment_method.change && !this.form.reference) {
        this.errors.reference = 'Debe ingresar una referencia'
        return false
      }

      // Para métodos de pago en moneda alternativa, validamos el monto en bolívares
      if (this.payment_method.change_currency_id) {
        if (!this.amountAlternate || parseFloat(this.amountAlternate) <= 0) {
          this.errors.amount_bs = 'El monto en bolívares debe ser mayor a cero'
          return false
        }
      } else {
        // Para pagos en dólares, validamos el monto en dólares
        if (!this.form.amount || parseFloat(this.form.amount) <= 0) {
          this.errors.amount = 'El monto en dólares debe ser mayor a cero'
          return false
        }
      }

      // Validar contra el balance proporcionado
      if (this.form.amount > this.balance && this.balance > 0) {
        this.$swal.fire({
          icon: 'warning',
          title: 'Monto excedido',
          text: 'El monto del pago excede el balance pendiente',
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#3085d6'
        })
        this.errors.amount = 'El monto excede el balance pendiente'
        return false
      }

      return true
    },

    processPay() {
      if (!this.validatePayment()) {
        return
      }

      // Crear el objeto de pago con nombres compatibles con Create.vue
      const payment = {
        method: this.payment_method.id,
        amount: this.payment_method.change_currency_id ? parseFloat(this.form.amount) || 0 : parseFloat(this.form.amount) || 0,
        amount_bs: this.payment_method.change_currency_id ? parseFloat(this.amountAlternate) || 0 : 0,
        exchange_rate: this.payment_method.change_currency_id ? parseFloat(this.change) || 0 : null,

        reference: this.form.reference || ''
      }
      
      // console.log('Enviando pago:', payment);

      // Emitir el evento con los datos del pago
      this.$emit('add-payment', payment)
      
      // Resetear el formulario
      this.resetForm()
    }
  }
}
</script>
